import React, { useEffect, useState } from "react";

import { FLOW_TYPES } from "../../../constants/flowType";
import { useTicketingContext } from "../../../store";
import { HELPERS, VISUAL_HELPERS } from "../../../utils";
import { Button, ExpandIcon, Select } from "../..";

const SummaryCollapsible = ({
  admissionTickets,
  isChanged,
  onContinue,
  onBack,
  dateText,
  onCurrencySelect,
  totals,
  isSummaryEmpty,
  isActiveStepValid,
  isFirstStep,
  isLastStep,
  isCartUpdateInProgress,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { state } = useTicketingContext();

  const { addons, currencies, dates, texts, summary } = state;

  const onChangeHandler = (value) => {
    onCurrencySelect(value);
  };

  const {
    selectedCurrency,
    selectedAdmissionTickets,
    selectedTimeslot,
    selectedDate,
    selectedAddonTickets,
    selectedGiftCardVariants,
  } = summary;

  const isGiftCardFlow = state.flowType === FLOW_TYPES.GIFT_CARD;
  const giftCardVariantList = selectedGiftCardVariants
    ? Object.values(selectedGiftCardVariants)
    : [];

  const sortedSelectedAdmissionTickets = selectedAdmissionTickets?.sort(
    (a, b) => a.ordinal - b.ordinal
  );

  const showBackButton =
    !isFirstStep && state.flowType === FLOW_TYPES.ADMISSION;

  const { active } = state.stepper;

  useEffect(() => {
    setIsExpanded(false);
  }, [active]);

  const totalNumberOfTickets =
    state.summary.selectedAdmissionTickets?.reduce(
      (acumulator, ticket) => ticket.qty + acumulator,
      0
    ) +
    Object.values(state.summary.selectedAddonTickets).reduce(
      (accumulator, ticket) => accumulator + ticket.qty,
      0
    );

  return (
    <div className="summary-collapsible">
      <div
        className="collapse-button"
        onClick={() => setIsExpanded((current) => !current)}
      >
        <div className="collapse-icon">
          <ExpandIcon
            width={25}
            height={25}
            rotationDegree={isExpanded ? "0" : "180"}
          />
        </div>
      </div>

      {isExpanded ? (
        <div className="expanded-content">
          <div className="summary-header-wrapper">
            <div className="summary-title expanded">{texts.summary.title}</div>
            <Select
              handleOnChange={onChangeHandler}
              id="currency"
              options={currencies.map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
              style={{
                position: "absolute",
                right: "30px",
                marginTop: "40px",
                zIndex: 1,
              }}
            />
          </div>

          <div className="scrollable-content">
            {isGiftCardFlow && giftCardVariantList.length > 0 ? (
              <div className="admission-tickets">
                <div className="subtitle-wrapper">
                  <div className="summary-subtitle">Gift Card</div>
                </div>
                {giftCardVariantList.map((variant) => (
                  <div className="admission-ticket" key={variant.id}>
                    <div className="admission-total">
                      <div>{`${variant.qty || 1} × ${variant.title} ${
                        variant.price.currencyCode
                      }`}</div>
                      <div>{`${
                        selectedCurrency.name
                      } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                        Number(variant.price.amount) * (variant.qty || 1),
                        selectedCurrency
                      )}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {Object.keys(selectedAdmissionTickets || {}).length > 0 ? (
              <div className="admission-tickets">
                <div className="subtitle-wrapper">
                  <div className="summary-subtitle">
                    {texts.summary.admission_tickets}
                  </div>
                  <div className="summary-selected-date">
                    {dateText}{" "}
                    {selectedTimeslot != null
                      ? `- ${VISUAL_HELPERS.displayTimeString(
                          HELPERS.getTimestampMillisBasedOnEntryNo(
                            dates,
                            selectedDate,
                            selectedTimeslot
                          )
                        )}`
                      : null}
                  </div>
                </div>

                {sortedSelectedAdmissionTickets.map((ticket) => {
                  const { id, qty } = ticket;
                  const name = HELPERS.getAdmissionTicketNameById(
                    id,
                    admissionTickets
                  );
                  const { min, max } = HELPERS.getPriceOfAdmissionTicketById(
                    id,
                    admissionTickets,
                    selectedDate,
                    dates
                  );
                  const freeText = texts.common.free;
                  const isIndicativeCalculation = selectedDate === null;
                  const totalPriceString = VISUAL_HELPERS.displayPriceText(
                    min,
                    max,
                    selectedCurrency,
                    freeText,
                    qty,
                    isIndicativeCalculation
                  );
                  const pricePerPerson =
                    VISUAL_HELPERS.displayPricePerPersonText(
                      min,
                      max,
                      selectedCurrency,
                      freeText,
                      qty,
                      isIndicativeCalculation
                    );
                  const showPerPeson = pricePerPerson !== freeText;
                  return (
                    <div className="admission-ticket" key={id}>
                      <div className="admission-total">
                        <div>{`${qty} ${name}`}</div>
                        <div>{totalPriceString}</div>
                      </div>
                      {showPerPeson ? (
                        <div className="admission-per-person">
                          <div>{`${pricePerPerson} ${texts.common.per_person}`}</div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {Object.keys(selectedAddonTickets).length > 0 ? (
              <div className="addon-tickets">
                <div className="splitter" />
                <div className="subtitle-wrapper">
                  <div className="summary-subtitle">
                    {texts.summary.addon_tickets}
                  </div>
                </div>

                {Object.keys(selectedAddonTickets).map((addonId) => {
                  const title = HELPERS.getAddonEventTitleById(addons, addonId);

                  const ticketInfoText =
                    VISUAL_HELPERS.displayAddonTicketInfoText(
                      addons,
                      addonId,
                      selectedAddonTickets[addonId],
                      selectedDate,
                      texts
                    );

                  let pricePerPerson, totalPrice;

                  const prices = totals.addonsTotalPrices.find(
                    (total) => total.addonId === addonId
                  );

                  if (prices) {
                    pricePerPerson = prices.pricePerPerson;
                    totalPrice = prices.totalPrice;
                  }
                  return (
                    <div className="addon-ticket" key={title}>
                      <div className="addon-total">
                        <div>{title}</div>
                        <div>{`${
                          selectedCurrency.name
                        } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                          totalPrice,
                          selectedCurrency
                        )}`}</div>
                      </div>
                      <div className="addon-per-person">
                        <div>{ticketInfoText}</div>
                        <div>{`${
                          selectedCurrency.name
                        } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                          pricePerPerson,
                          selectedCurrency
                        )} ${texts.common.per_person}`}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="splitter" />
        </div>
      ) : (
        <div>
          <div className="summary-title collapsed">{texts.summary.title}</div>
        </div>
      )}

      <div className="content">
        {isSummaryEmpty ? (
          <div className="empty-summary">
            <p>{texts.summary.no_items}</p>
          </div>
        ) : (
          <div className="totals-wrapper">
            <div className="totals-row">
              <div>
                {texts.summary.total}
                {!isGiftCardFlow
                  ? ` (${totalNumberOfTickets} ${texts.summary.tickets})`
                  : ""}
              </div>
              <div>{`${
                selectedCurrency.name
              } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                totals.totalWithDiscount,
                selectedCurrency
              )}`}</div>
            </div>
          </div>
        )}
      </div>

      <div className={`button-group ${!showBackButton ? "single-button" : ""}`}>
        {showBackButton ? (
          <Button
            color="secondary"
            label={texts.buttons.back}
            onClick={() => {
              setIsExpanded(false);
              onBack();
            }}
          />
        ) : null}

        {isLastStep ? (
          <Button
            color="primary"
            disabled={isCartUpdateInProgress || !isActiveStepValid}
            label={texts.buttons.check_out}
            onClick={onContinue}
            showSpinner={isCartUpdateInProgress}
          />
        ) : (
          <Button
            color="primary"
            disabled={!isActiveStepValid || isCartUpdateInProgress}
            label={isLastStep ? "checkout" : texts.buttons.continue}
            onClick={onContinue}
            showSpinner={isCartUpdateInProgress}
          />
        )}
      </div>
    </div>
  );
};

export default SummaryCollapsible;
